'use client';

import { SignInButton, SignedIn, SignedOut, UserButton, SignOutButton } from '@clerk/nextjs';
import { usePathname, useRouter } from 'next/navigation';
import Logo from '@/components/shared/Logo';
import Link from 'next/link';
import { useState, useRef } from 'react';
import {
  Menu,
  X,
  Search,
  Store,
  FileText,
  Users,
  ShoppingCart,
  Settings,
  LogOut,
  LayoutDashboard,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import DarkModeToggle from '@/components/shared/DarkModeToggle';

function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  const handleSearchBlur = (e: React.FocusEvent) => {
    if (e.relatedTarget === null || !(e.currentTarget as Node).contains(e.relatedTarget as Node)) {
      setIsSearchOpen(false);
    }
  };

  const MobileMenu = () => (
    <div
      className={`lg:hidden fixed inset-0 bg-background z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
        isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <div className="flex flex-col min-h-full">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-background z-10">
          <Logo />
          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-md hover:bg-accent"
            aria-label="Close menu"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 p-4 space-y-4">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground px-2 mb-2">Marketplace</h3>
            <div className="space-y-1">
              <Link
                href="/lists"
                onClick={toggleMobileMenu}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  pathname?.startsWith('/lists')
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent/60'
                }`}
              >
                <FileText className="w-4 h-4" />
                Browse Lead Lists
              </Link>
              <Link
                href="/scrapers"
                onClick={toggleMobileMenu}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  pathname?.startsWith('/scrapers')
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent/60'
                }`}
              >
                <Store className="w-4 h-4" />
                Browse Scrapers
              </Link>
              <Link
                href="/group-buys"
                onClick={toggleMobileMenu}
                className={`flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                  pathname?.startsWith('/group-buys')
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent/60'
                }`}
              >
                <Users className="w-4 h-4" />
                Browse Group Buys
              </Link>
            </div>
          </div>

          <SignedIn>
            <div className="space-y-2 mt-4 pt-4 border-t border-border">
              <h3 className="text-sm font-semibold text-muted-foreground px-2 mb-2">My Account</h3>
              <div className="space-y-1">
                <Link
                  href="/dashboard"
                  onClick={toggleMobileMenu}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-accent/60"
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Dashboard
                </Link>
                <Link
                  href="/dashboard/lead-lists"
                  onClick={toggleMobileMenu}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-accent/60"
                >
                  <FileText className="w-4 h-4" />
                  My Lead Lists
                </Link>
                <Link
                  href="/dashboard/scrapers"
                  onClick={toggleMobileMenu}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-accent/60"
                >
                  <Store className="w-4 h-4" />
                  My Scraper Listings
                </Link>
                <Link
                  href="/dashboard/group-buys"
                  onClick={toggleMobileMenu}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-accent/60"
                >
                  <Users className="w-4 h-4" />
                  My Group Buys
                </Link>
                <Link
                  href="/dashboard/purchases"
                  onClick={toggleMobileMenu}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm font-medium rounded-md hover:bg-accent/60"
                >
                  <ShoppingCart className="w-4 h-4" />
                  My Purchases
                </Link>
              </div>
            </div>
          </SignedIn>
        </div>

        <div className="p-4 border-t mt-auto sticky bottom-0 bg-background z-10">
          <SignedIn>
            <div className="flex items-center gap-2">
              <UserButton />
              <span className="text-sm font-medium">Account</span>
            </div>
          </SignedIn>
          <SignedOut>
            <div className="flex gap-2">
              <SignInButton mode="modal">
                <button
                  onClick={toggleMobileMenu}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-3 text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors duration-200"
                >
                  Sign In
                </button>
              </SignInButton>
            </div>
          </SignedOut>
        </div>
      </div>
    </div>
  );

  return (
    <div className="border-b bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-6 p-5">
          <div className="flex items-center justify-between w-full lg:w-auto h-12 relative">
            <div
              className={`flex items-center justify-between w-full lg:w-auto transition-opacity duration-300 ${isSearchOpen ? 'lg:opacity-100 opacity-0 absolute pointer-events-none' : 'opacity-100'}`}
            >
              <Logo />
              <div className="flex items-center gap-2 lg:hidden">
                <button
                  onClick={() => {
                    setIsSearchOpen(true);
                    setTimeout(() => searchInputRef.current?.focus(), 50);
                  }}
                  className="p-2 rounded-md hover:bg-accent"
                  aria-label="Open search"
                >
                  <Search className="w-6 h-6" />
                </button>
                <button
                  onClick={toggleMobileMenu}
                  className="p-2 rounded-md hover:bg-accent"
                  aria-label="Open menu"
                >
                  <Menu className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div
              className={`w-full lg:hidden flex items-center transition-all duration-300 ease-in-out ${
                isSearchOpen
                  ? 'opacity-100 max-w-full'
                  : 'opacity-0 max-w-0 overflow-hidden absolute pointer-events-none'
              }`}
            >
              <form onSubmit={handleSearch} className="relative w-full" onBlur={handleSearchBlur}>
                <button
                  type="button"
                  onClick={() => setIsSearchOpen(false)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-accent z-10"
                  aria-label="Close search"
                >
                  <X className="w-5 h-5" />
                </button>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for lists, scrapers, group buys, and services..."
                  className="w-full pl-10 pr-10 py-2 h-10 rounded-lg border border-[hsl(var(--border))] bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-accent z-10"
                  aria-label="Search"
                >
                  <Search className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>

          <div className="hidden lg:flex items-center gap-6 flex-1 max-w-2xl mx-8">
            <form onSubmit={handleSearch} className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-5 h-5" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for lists, scrapers, group buys, and services..."
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-[hsl(var(--border))] bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </form>
            <DarkModeToggle />
          </div>

          <div className="hidden lg:flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-foreground hover:text-primary">
                Marketplace
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem asChild>
                  <Link
                    href="/lists"
                    className="flex items-center gap-2 border-none outline-none ring-0"
                  >
                    <FileText className="w-4 h-4" />
                    Browse Lead Lists
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    href="/scrapers"
                    className="flex items-center gap-2 border-none outline-none ring-0"
                  >
                    <Store className="w-4 h-4" />
                    Browse Scrapers
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link
                    href="/group-buys"
                    className="flex items-center gap-2 border-none outline-none ring-0"
                  >
                    <Users className="w-4 h-4" />
                    Browse Group Buys
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <SignedIn>
              <Link
                href="/dashboard"
                className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90"
              >
                Dashboard
              </Link>
            </SignedIn>
            <SignedIn>
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center justify-center">
                  <UserButton
                    appearance={{
                      elements: {
                        avatarBox: 'w-8 h-8 flex items-center justify-center',
                      },
                    }}
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link
                      href="/dashboard"
                      className="flex items-center gap-2 border-none outline-none ring-0"
                    >
                      <LayoutDashboard className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link
                      href="/dashboard/lead-lists"
                      className="flex items-center gap-2 border-none outline-none ring-0"
                    >
                      <FileText className="w-4 h-4" />
                      My Lead Lists
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/dashboard/scrapers"
                      className="flex items-center gap-2 border-none outline-none ring-0"
                    >
                      <Store className="w-4 h-4" />
                      My Scraper Listings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/dashboard/group-buys"
                      className="flex items-center gap-2 border-none outline-none ring-0"
                    >
                      <Users className="w-4 h-4" />
                      My Group Buys
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      href="/dashboard/purchases"
                      className="flex items-center gap-2 border-none outline-none ring-0"
                    >
                      <ShoppingCart className="w-4 h-4" />
                      My Purchases
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <SignOutButton>
                      <span className="flex items-center gap-2 text-destructive w-full px-2 py-2 border-none">
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </span>
                    </SignOutButton>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SignedIn>
            <SignedOut>
              <div className="flex items-center gap-2">
                <SignInButton mode="modal">
                  <button className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90">
                    Sign In
                  </button>
                </SignInButton>
              </div>
            </SignedOut>
          </div>
        </div>
      </div>
      {MobileMenu()}
    </div>
  );
}

export default Header;
